import { useState, useEffect, useCallback } from 'react';
import { TranslationType } from '../components/locales/types';
import { 
  fetchNavigationTranslationsForLanguage,
  setPreferredLanguage,
  getCurrentLanguageFromCookies
} from '../utils/navigationApi';

interface UseNavigationTranslationsReturn {
  translations: TranslationType;
  isLoading: boolean;
  error: string | null;
  language: string;
  changeLanguage: (lang: string) => Promise<boolean>;
}

// Default empty translations structure
const emptyTranslations: TranslationType = {
  navigations: {
    header: {
      home: '',
      news: '',
      entertainment: '',
      sports: '',
      signIn: ''
    },
    footer: {
      about: '',
      contact: '',
      privacy: '',
      terms: '',
      copyright: '',
      followUs: '',
      subscribe: ''
    },
    home: {
      title: ''
    },
    about: {
      title: '',
      description: ''
    },
    notFound: {
      title: '',
      subtitle: '',
      homeLink: ''
    }
  }
};

// Map of supported language codes - normalizes input language codes to supported ones
const LANGUAGE_CODE_MAP: Record<string, string> = {
  'en': 'en',
  'en-US': 'en',
  'en-GB': 'en',
  'zh-CN': 'cn',
  'zh-TW': 'tw',
  'es': 'es',
  'es-ES': 'es',
  'es-MX': 'es',
  'fr': 'fr',
  'fr-FR': 'fr',
  'fr-CA': 'fr'
};

// Static variables shared across all hook instances
// This ensures translations are loaded only once application-wide
const globalTranslationsCache: Record<string, TranslationType> = {};
let isGloballyInitialized = false;
let currentGlobalLanguage = 'en';
let isCurrentlyLoading = false; // Flag to prevent concurrent loading requests

// Add a timestamp to track when each language was last requested
const lastLanguageRequestTimestamp: Record<string, number> = {};
const DEBOUNCE_INTERVAL = 2000; // 2 seconds

// Track successful language changes to help coordinating components
const languageChangeCompletions: Record<string, boolean> = {};

/**
 * Detect browser language and normalize it to a supported language
 */
function detectBrowserLanguage(): string {
  try {
    // Get browser language
    const browserLang = navigator.language || 
                       (navigator as any).userLanguage || 
                       (navigator as any).browserLanguage || 
                       'en';
    
    console.log(`Detected browser language: ${browserLang}`);
    return normalizeLanguage(browserLang);
  } catch (err) {
    console.warn('Failed to detect browser language:', err);
    return 'en';
  }
}

/**
 * Normalize language code to a supported language
 */
function normalizeLanguage(lang: string): string {
  if (!lang) return 'en';
  
  // Handle case where lang might be null or undefined
  const langStr = String(lang).trim();
  
  console.log(`Normalizing language code: "${langStr}"`);
  
  // Special handling for English - any variation of English returns 'en'
  if (langStr === 'en' || langStr.toLowerCase().startsWith('en')) {
    console.log('Language identified as English');
    return 'en';
  }
  
  // Direct matches for other base language codes
  // if (langStr === 'zh-CN') return 'zh-CN';
  // if (langStr === 'zh-TW') return 'zh-TW';
  if (langStr === 'cn') return 'cn';
  if (langStr === 'tw') return 'tw';
  if (langStr === 'es') return 'es';
  if (langStr === 'fr') return 'fr';
  
  // Then check mapping for variants
  if (LANGUAGE_CODE_MAP[langStr]) {
    console.log(`Found in language map: ${langStr} → ${LANGUAGE_CODE_MAP[langStr]}`);
    return LANGUAGE_CODE_MAP[langStr];
  }
  
  // Then check if it's a partial match (e.g., "zh-cn" matches "zh-CN")
  for (const [key, value] of Object.entries(LANGUAGE_CODE_MAP)) {
    if (langStr.toLowerCase().startsWith(key.toLowerCase())) {
      console.log(`Found partial match: ${langStr} → ${value}`);
      return value;
    }
  }
  
  // Default to English if no match
  console.warn(`Unknown language code "${langStr}", defaulting to English`);
  return 'en';
}

/**
 * Hook for accessing and managing navigation translations
 * Translations are loaded only once on initial mount and when language is changed
 */
export function useNavigationTranslations(): UseNavigationTranslationsReturn {
  const [translations, setTranslations] = useState<TranslationType>(
    globalTranslationsCache[currentGlobalLanguage] || emptyTranslations
  );
  const [isLoading, setIsLoading] = useState(!isGloballyInitialized || isCurrentlyLoading);
  const [error, setError] = useState<string | null>(null);
  const [language, setLanguage] = useState(currentGlobalLanguage);
  
  // Initialize the language from cookies or browser language
  useEffect(() => {
    // Skip if already initialized or we're already in the process of loading
    if (isGloballyInitialized || isCurrentlyLoading) return;
    
    // Check for language in cookies first
    const cookieLanguage = getCurrentLanguageFromCookies();
    if (cookieLanguage) {
      console.log(`Using language from cookie: ${cookieLanguage}`);
      currentGlobalLanguage = normalizeLanguage(cookieLanguage);
    } else {
      // Fall back to browser language
      console.log('No cookie language found, detecting browser language');
      currentGlobalLanguage = detectBrowserLanguage();
    }
    
    console.log(`Initial language set to: ${currentGlobalLanguage}`);
  }, []);

  // Sync component state with global state
  useEffect(() => {
    // If global language changed, update local state
    if (language !== currentGlobalLanguage && isGloballyInitialized) {
      console.log(`Syncing component language from ${language} to global ${currentGlobalLanguage}`);
      setLanguage(currentGlobalLanguage);
      setTranslations(globalTranslationsCache[currentGlobalLanguage] || emptyTranslations);
    }
  }, [language, currentGlobalLanguage, isGloballyInitialized]);

  // Load initial translations only once application-wide when the first component mounts
  useEffect(() => {
    let isMounted = true;
    let timeoutId: NodeJS.Timeout;

    async function loadInitialTranslations() {
      // Skip if translations already loaded globally or if another loading operation is in progress
      if (isGloballyInitialized || isCurrentlyLoading) {
        console.log('Using existing translations from global cache or already loading');
        return;
      }

      // Set loading flag to prevent concurrent requests
      isCurrentlyLoading = true;
      console.log('Loading initial translations (first time)...');
      setIsLoading(true);
      setError(null);
      
      // Language should already be set from the initialization effect
      console.log(`Loading translations for language: ${currentGlobalLanguage}`);
      
      // Set a timeout to fall back to empty translations if loading takes too long
      timeoutId = setTimeout(() => {
        if (isCurrentlyLoading && isMounted) {
          console.warn('Translation loading timed out, using empty translations');
          isCurrentlyLoading = false;
          isGloballyInitialized = true;
          globalTranslationsCache[currentGlobalLanguage] = emptyTranslations;
          setTranslations(emptyTranslations);
          setLanguage(currentGlobalLanguage);
          setIsLoading(false);
        }
      }, 5000); // 5 second timeout
      
      try {
        // Fetch translations for the determined language
        console.log(`Fetching initial translations for language: ${currentGlobalLanguage}`);
        const data = await fetchNavigationTranslationsForLanguage(currentGlobalLanguage);
        
        // Only update state if component is still mounted and we haven't timed out
        if (isMounted && isCurrentlyLoading) {
          console.log(`Received initial translations for ${currentGlobalLanguage}`);
          clearTimeout(timeoutId);
          
          if (data && data.navigations) {
            // Cache translations globally
            globalTranslationsCache[currentGlobalLanguage] = data;
            isGloballyInitialized = true;
            
            // Update component state
            setTranslations(data);
            setLanguage(currentGlobalLanguage);
          } else {
            throw new Error("Received invalid translation data");
          }
        }
      } catch (err) {
        console.error(`Error loading translations for ${currentGlobalLanguage}:`, err);
        if (isMounted && isCurrentlyLoading) {
          clearTimeout(timeoutId);
          setError('Failed to load translations');
          
          // Fallback to empty translations
          isGloballyInitialized = true;
          globalTranslationsCache[currentGlobalLanguage] = emptyTranslations;
          setTranslations(emptyTranslations);
          setLanguage(currentGlobalLanguage);
        }
      } finally {
        if (isMounted) {
          clearTimeout(timeoutId);
          isCurrentlyLoading = false;
          setIsLoading(false);
        }
      }
    }

    loadInitialTranslations();

    // Cleanup function to handle component unmounting
    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, []); // Empty dependency array means this runs once on mount

  // Function to change the language
  const changeLanguage = useCallback(async (lang: string): Promise<boolean> => {
    // Normalize the language code consistently 
    const normalizedLang = normalizeLanguage(lang);
    
    const now = Date.now();
    console.log(`Beginning language change from ${language} to ${normalizedLang}`);
    
    // Don't do anything if it's the same language or if currently loading
    if (language === normalizedLang || isCurrentlyLoading) {
      console.log(`Skip language change: same language or already loading`);
      return true; // Consider it successful if already set
    }
    
    // Check if this language was requested recently to prevent duplicate calls
    const lastRequestTime = lastLanguageRequestTimestamp[normalizedLang];
    if (lastRequestTime && (now - lastRequestTime) < DEBOUNCE_INTERVAL) {
      console.log(`Language ${normalizedLang} was requested recently (${now - lastRequestTime}ms ago), skipping duplicate request`);
      // Return the completion status from the previous call
      return !!languageChangeCompletions[normalizedLang];
    }
    
    // Record this request time
    lastLanguageRequestTimestamp[normalizedLang] = now;
    // Reset completion status
    languageChangeCompletions[normalizedLang] = false;
    
    // OPTIMIZATION: Check if we have cached translations first
    // If we do, update state immediately for better UX
    if (globalTranslationsCache[normalizedLang]) {
      console.log(`Using cached translations for ${normalizedLang} immediately`);
      // Update component state right away for instant UI update
      setLanguage(normalizedLang);
      setTranslations(globalTranslationsCache[normalizedLang]);
      
      // Still need to update the cookie, but UI can reflect change immediately
      currentGlobalLanguage = normalizedLang;
    }
    
    // Set loading flag to prevent concurrent requests
    isCurrentlyLoading = true;
    setIsLoading(true);
    setError(null);
    
    try {
      // 1. Set the preferred language via the API (this sets the cookie)
      console.log(`Setting preferred language cookie for: ${normalizedLang}`);
      
      // Check if we're setting the same language that's already the global language
      if (normalizedLang === currentGlobalLanguage) {
        console.log(`Language ${normalizedLang} is already the current global language, skipping API call`);
      } else {
        const result = await setPreferredLanguage(normalizedLang);
        
        if (!result || result.status !== 'success') {
          console.error('Failed to set language preference:', result?.message || 'Unknown error');
          throw new Error(result?.message || 'Failed to set language preference');
        }
      }
      
      // 2. If we already used cached translations above, we can skip fetching
      if (globalTranslationsCache[normalizedLang]) {
        console.log(`Already updated UI with cached translations for ${normalizedLang}`);
        languageChangeCompletions[normalizedLang] = true;
        return true;
      }
      
      // 3. Fetch translations for the new language
      console.log(`Fetching fresh translations for ${normalizedLang}`);
      const data = await fetchNavigationTranslationsForLanguage(normalizedLang);
      
      if (!data || !data.navigations) {
        console.error(`Invalid translation data received for ${normalizedLang}`);
        throw new Error("Received invalid translation data");
      }
      
      // 4. Cache the translations globally
      console.log(`Caching translations for ${normalizedLang}`);
      globalTranslationsCache[normalizedLang] = data;
      
      // 5. Update global and component state
      currentGlobalLanguage = normalizedLang;
      setTranslations(data);
      setLanguage(normalizedLang);
      
      console.log(`Language successfully changed to ${normalizedLang}`);
      languageChangeCompletions[normalizedLang] = true;
      return true;
      
    } catch (err) {
      console.error(`Error changing language to ${normalizedLang}:`, err);
      setError(`Failed to change language to ${normalizedLang}`);
      
      // Still update the language even if we couldn't fetch new translations
      currentGlobalLanguage = normalizedLang;
      setLanguage(normalizedLang);
      
      // Use empty translations as fallback
      if (!globalTranslationsCache[normalizedLang]) {
        console.log(`Using empty translations as fallback for ${normalizedLang}`);
        globalTranslationsCache[normalizedLang] = emptyTranslations;
        setTranslations(emptyTranslations);
      }
      
      languageChangeCompletions[normalizedLang] = true;
      return true; // Consider it complete even with error since we've updated the state
    } finally {
      // Always clean up loading state
      isCurrentlyLoading = false;
      setIsLoading(false);
    }
  }, [language]);

  return {
    translations,
    isLoading,
    error,
    language,
    changeLanguage
  };
}

// Export a helper function to check if translations exist for a language
export function hasTranslationsForLanguage(lang: string): boolean {
  const normalizedLang = normalizeLanguage(lang);
  return !!globalTranslationsCache[normalizedLang];
} 